import { Add, FileDownload, SyncAltOutlined } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import ExcelJS from 'exceljs'
import { useEffect, useMemo, useState } from 'react'
import { useRepCodes, useUser } from '../hooks'
import './RepCodes.css'
import { repCodeType } from '../types'
import NewRepCode from './NewRepCode'
import dayjs from 'dayjs'
import axios from 'axios'

const COLUMN_WIDTHS = {
  repCode: undefined,
  active: '140px',
  accountExecutive: '11%',
  aeEmail: '15%',
  insideSalesRep: '11%',
  isrEmail: '15%',
  engineer: '11%',
  engEmail: '15%',
}

export default function RepCodes() {
  const user = useUser()
  const editor = useMemo(
    () => user.admin || user.operations || user.email === 'ckubacki@imprestechnology.com',
    [user.admin, user.operations, user.email],
  )
  const [repCodes, _setRepCodes] = useRepCodes()

  const [filteredRepCodes, setFilteredRepCodes] = useState<repCodeType[]>(structuredClone(repCodes))
  const [filters, setFilters] = useState({
    repCode: '',
    active: '',
    accountExecutive: '',
    insideSalesRep: '',
    engineer: '',
    page: 0,
    count: 25,
    sort: 'RepCode asc',
  })
  const [newRepCodeMode, setNewRepCodeMode] = useState<'add' | 'edit' | null>(null)
  const [selectedRepCode, setSelectedRepCode] = useState<repCodeType | undefined>(undefined)
  const [exportStatus, setExportStatus] = useState<'success' | 'error' | 'processing' | ''>('')
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportType, setExportType] = useState<string>('all')
  const [syncStatus, setSyncStatus] = useState<'success' | 'error' | 'processing' | ''>('')
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)

  useEffect(() => {
    document.title = 'Rep Codes | Order Portal'
  }, [])

  useEffect(() => {
    setFilteredRepCodes(structuredClone(repCodes))
    handleSearch(filters)
  }, [repCodes])

  const handleSearch = (filterParam: typeof filters) => {
    setFilteredRepCodes(
      repCodes.filter(repCode => {
        if (filterParam.repCode && !repCode.RepCode.includes(filterParam.repCode)) return false
        if (filterParam.active && repCode.IsActive !== (filterParam.active === 'Yes')) return false
        if (filterParam.accountExecutive && repCode.AccountExecutive !== filterParam.accountExecutive) return false
        if (filterParam.insideSalesRep && repCode.InsideSalesRep !== filterParam.insideSalesRep) return false
        if (filterParam.engineer && repCode.Engineer !== filterParam.engineer) return false
        return true
      }),
    )
    handleSort(filterParam.sort)
  }

  const handleSort = (sort: string) => {
    setFilteredRepCodes(prev =>
      prev.sort((a, b) => {
        if (sort.includes('desc')) {
          if ((a[sort.split(' ')[0]] ?? '') === (b[sort.split(' ')[0]] ?? '')) return a.RepCode < b.RepCode ? 1 : -1
          return (a[sort.split(' ')[0]] ?? '') < (b[sort.split(' ')[0]] ?? '') ? 1 : -1
        } else {
          if ((a[sort.split(' ')[0]] ?? '') === (b[sort.split(' ')[0]] ?? '')) return a.RepCode > b.RepCode ? 1 : -1
          return (a[sort.split(' ')[0]] ?? '') > (b[sort.split(' ')[0]] ?? '') ? 1 : -1
        }
      }),
    )
  }

  const exportRepCodes = async (exportType: string) => {
    setExportStatus('processing')
    try {
      const headers: string[] = [
        'Rep Code',
        'Active',
        'Account Executive',
        'AE Email',
        'Inside Sales Rep',
        'ISR Email',
        'Engineer',
        'Eng Email',
      ]
      const repCodesToExport = exportType === 'all' ? repCodes : filteredRepCodes
      const rows = repCodesToExport.map(rc => [
        rc.RepCode,
        rc.IsActive ? 'Yes' : 'No',
        rc.AccountExecutive ?? '',
        rc.AeEmail ?? '',
        rc.InsideSalesRep,
        rc.IsrEmail ?? '',
        rc.Engineer ?? '',
        rc.EngEmail ?? '',
      ])
      const workbook = new ExcelJS.Workbook()
      const sheet = workbook.addWorksheet('Rep Codes')
      sheet.addRow(headers)
      rows.forEach(row => {
        sheet.addRow(row)
      })
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const dateStamp = dayjs(new Date()).format('YYYY-MM-DD')
      link.download = `rep-codes-${dateStamp}.xlsx`
      link.click()
      URL.revokeObjectURL(url)
      setExportStatus('success')
      setExportDialogOpen(false)
    } catch (error) {
      console.error(error)
      setExportStatus('error')
    }
  }
  const syncRepCodes = async () => {
    setSyncStatus('processing')
    try {
      await axios.post('/repCodes/syncAll?dryRun=false')
      setSyncStatus('success')
      setSyncDialogOpen(false)
    } catch (error) {
      console.error(error)
      setSyncStatus('error')
    }
  }

  return (
    <div className="rep-codes">
      <div className="rep-codes-filters">
        <Grid container spacing="24px">
          <Grid size={2.4}>
            <div className="rep-codes-filters-grid-item">
              <Typography variant="body2">Rep Code</Typography>
              <TextField
                fullWidth
                label="Rep Code"
                size="small"
                value={filters.repCode}
                onChange={e => setFilters({ ...filters, repCode: e.target.value })}
              />
            </div>
          </Grid>
          <Grid size={2.4}>
            <div className="rep-codes-filters-grid-item">
              <Typography variant="body2">Active</Typography>
              <TextField
                select
                fullWidth
                label="Active"
                size="small"
                value={filters.active}
                onChange={e => setFilters({ ...filters, active: e.target.value })}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </div>
          </Grid>
          <Grid size={2.4}>
            <div className="rep-codes-filters-grid-item">
              <Typography variant="body2">Account Executive</Typography>
              <Autocomplete
                fullWidth
                size="small"
                options={Array.from(new Set(repCodes.map(repCode => repCode.AccountExecutive).filter(Boolean)))}
                getOptionLabel={option => option ?? ''}
                onChange={(_, value) => setFilters({ ...filters, accountExecutive: value ?? '' })}
                renderInput={params => <TextField {...params} label="Account Executive" />}
                value={filters.accountExecutive}
              />
            </div>
          </Grid>
          <Grid size={2.4}>
            <div className="rep-codes-filters-grid-item">
              <Typography variant="body2">Inside Sales Rep</Typography>
              <Autocomplete
                fullWidth
                size="small"
                options={Array.from(new Set(repCodes.map(repCode => repCode.InsideSalesRep).filter(Boolean)))}
                getOptionLabel={option => option}
                onChange={(_, value) => setFilters({ ...filters, insideSalesRep: value ?? '' })}
                renderInput={params => <TextField {...params} label="Inside Sales Rep" />}
                value={filters.insideSalesRep}
              />
            </div>
          </Grid>
          <Grid size={2.4}>
            <div className="rep-codes-filters-grid-item">
              <Typography variant="body2">Engineer</Typography>
              <Autocomplete
                fullWidth
                size="small"
                options={Array.from(new Set(repCodes.map(repCode => repCode.Engineer).filter(Boolean)))}
                getOptionLabel={option => option ?? ''}
                onChange={(_, value) => setFilters({ ...filters, engineer: value ?? '' })}
                renderInput={params => <TextField {...params} label="Engineer" />}
                value={filters.engineer}
              />
            </div>
          </Grid>
        </Grid>
        <div className="rep-codes-filters-buttons-container">
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              const newFilters = {
                ...filters,
                repCode: '',
                active: '',
                accountExecutive: '',
                insideSalesRep: '',
                engineer: '',
                sort: 'RepCode asc',
              }
              setFilters(newFilters)
              handleSearch(newFilters)
            }}
          >
            Clear
          </Button>
          <Button variant="contained" size="large" onClick={() => handleSearch(filters)}>
            Search
          </Button>
        </div>
      </div>
      <div className="rep-codes-table">
        <div className="rep-codes-table-pagination-header">
          <div className="rep-codes-table-pagination-header-left">
            <Typography variant="h6">Rep Codes</Typography>
            {editor && (
              <Button variant="contained" size="large" startIcon={<Add />} onClick={() => setNewRepCodeMode('add')}>
                Add New
              </Button>
            )}
            <Button
              variant="outlined"
              size="large"
              startIcon={<FileDownload />}
              onClick={() => setExportDialogOpen(true)}
            >
              Export
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Button
              variant="contained"
              size="large"
              startIcon={<SyncAltOutlined />}
              onClick={() => setSyncDialogOpen(true)}
              loading={syncStatus === 'processing'}
            >
              Sync All
            </Button>
          </div>
          <TablePagination
            component="div"
            count={filteredRepCodes.length}
            rowsPerPage={filters.count}
            page={filters.page}
            onPageChange={(_, page) => setFilters({ ...filters, page })}
            onRowsPerPageChange={e => setFilters({ ...filters, count: parseInt(e.target.value) })}
          />
        </div>
        <Table stickyHeader color="primary">
          <TableHead>
            <TableRow>
              <TableCell width={COLUMN_WIDTHS.repCode}>
                <TableSortLabel
                  active={filters.sort.includes('RepCode')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'RepCode ' + (filters.sort === 'RepCode asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  Rep Code
                </TableSortLabel>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.active}>Active</TableCell>
              <TableCell width={COLUMN_WIDTHS.accountExecutive}>
                <TableSortLabel
                  active={filters.sort.includes('AccountExecutive')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'AccountExecutive ' + (filters.sort === 'AccountExecutive asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  Account Executive
                </TableSortLabel>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.aeEmail}>
                <TableSortLabel
                  active={filters.sort.includes('AeEmail')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'AeEmail ' + (filters.sort === 'AeEmail asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  AE Email
                </TableSortLabel>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.insideSalesRep}>
                <TableSortLabel
                  active={filters.sort.includes('InsideSalesRep')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'InsideSalesRep ' + (filters.sort === 'InsideSalesRep asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  Inside Sales Rep
                </TableSortLabel>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.isrEmail}>
                <TableSortLabel
                  active={filters.sort.includes('IsrEmail')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'IsrEmail ' + (filters.sort === 'IsrEmail asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  ISR Email
                </TableSortLabel>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.engineer}>
                <TableSortLabel
                  active={filters.sort.includes('Engineer')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'Engineer ' + (filters.sort === 'Engineer asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  Engineer
                </TableSortLabel>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.engEmail}>
                <TableSortLabel
                  active={filters.sort.includes('EngEmail')}
                  direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  onClick={() => {
                    const newSort = 'EngEmail ' + (filters.sort === 'EngEmail asc' ? 'desc' : 'asc')
                    handleSort(newSort)
                    setFilters({ ...filters, sort: newSort })
                  }}
                >
                  Eng Email
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repCodes.length === 0
              ? Array.from({ length: filters.count }).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from({ length: 8 }).map((_, index) => (
                      <TableCell key={index}>
                        <Skeleton variant="text" width="100%" height="100%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : filteredRepCodes
                  .slice(filters.page * filters.count, (filters.page + 1) * filters.count)
                  .map(repCode => (
                    <TableRow key={repCode.RepCode}>
                      <TableCell>
                        <Link
                          onClick={() => {
                            setSelectedRepCode(repCode)
                            setNewRepCodeMode('edit')
                          }}
                        >
                          {repCode.RepCode}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={repCode.IsActive ? 'Active' : 'Inactive'}
                          color={repCode.IsActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{repCode.AccountExecutive}</TableCell>
                      <TableCell>{repCode.AeEmail}</TableCell>
                      <TableCell>{repCode.InsideSalesRep}</TableCell>
                      <TableCell>{repCode.IsrEmail}</TableCell>
                      <TableCell>{repCode.Engineer}</TableCell>
                      <TableCell>{repCode.EngEmail}</TableCell>
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </div>
      <NewRepCode
        mode={newRepCodeMode}
        editRepCode={selectedRepCode}
        onClose={() => {
          setNewRepCodeMode(null)
          setSelectedRepCode(undefined)
        }}
      />
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Rep Codes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Please select which rep codes you would like to export.</Typography>
          <RadioGroup name="export-type" value={exportType} onChange={e => setExportType(e.target.value)}>
            <FormControlLabel value="all" control={<Radio />} label="All" />
            <FormControlLabel value="filtered" control={<Radio />} label="Filtered (Current Search)" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setExportDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void exportRepCodes(exportType)
            }}
            loading={exportStatus === 'processing'}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <DialogTitle>Sync Rep Codes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            This will create or update all rep codes in Netsuite. Any rep code in Netsuite that is not in the Order
            Portal will be marked inactive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSyncDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void syncRepCodes()
            }}
            loading={syncStatus === 'processing'}
          >
            Sync
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={exportStatus === 'success'}
        autoHideDuration={5000}
        onClose={() => setExportStatus('')}
        message="Rep codes exported successfully"
      />
      <Snackbar
        open={exportStatus === 'error'}
        autoHideDuration={5000}
        onClose={() => setExportStatus('')}
        message="Failed to export rep codes. Please try again or contact support if the problem persists."
      />
      <Snackbar
        open={syncStatus === 'success'}
        autoHideDuration={5000}
        onClose={() => setSyncStatus('')}
        message="Rep codes synced successfully"
      />
      <Snackbar open={syncStatus === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setSyncStatus('')}>
          Failed to sync rep codes. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </div>
  )
}
